import { Box, Checkbox, CircularProgress, FormControlLabel, Grid, IconButton, LinearProgress, TextField, Typography } from "@mui/material";
import React, { CSSProperties, FC, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDropzone } from "react-dropzone";
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useStyles } from "./styles";
import { useDocumentAttachment, ProcessedImage } from "hooks/useDocumentAttachment";
import { FileChip } from "./components/FileChip";
import { ImagePreviews } from "./components/ImagePreviews";
import { PromptSelector } from "./components/PromptSelector";
import type { PromptSelectorProps } from "./components/PromptSelector";

type Props = {
  contentIsLoading: boolean
  onSendPrompt: (prompt: string, images: ProcessedImage[]) => void,
  isRecording: boolean,
  submitRecordedAudio: (images: ProcessedImage[]) => void,
  startRecordAudio: () => void,
  onViewImagePreview: (image: string) => void,
  textColor?: CSSProperties["color"],
  toogleAudioEnable: () => void,
  promptSelector: PromptSelectorProps,
  isAudioActive?: boolean,
  onStopAudio?: () => void,
}

export const PromptInput: FC<Props> = ({
  contentIsLoading,
  onSendPrompt,
  isRecording,
  submitRecordedAudio,
  startRecordAudio,
  onViewImagePreview,
  toogleAudioEnable,
  promptSelector,
  textColor = '#000000',
  isAudioActive = false,
  onStopAudio
}) => {
  const questionInputRef = useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    state: attachmentState,
    processFile,
    removeFile,
    toggleSelection,
    getSelectedImages,
    updateSendAs,
  } = useDocumentAttachment();

  const handleSend = () => {
    const text = questionInputRef.current?.value ?? '';
    const selectedImages = getSelectedImages();

    if (!text && selectedImages.length === 0) return;

    if (attachmentState.images.length > 0 && selectedImages.length === 0) {
      if (!text) return;
      onSendPrompt(text, []);
    } else {
      onSendPrompt(text, selectedImages);
    }

    removeFile();
    if (questionInputRef.current) {
      questionInputRef.current.value = '';
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open
  } = useDropzone({
    noClick: true,
    multiple: false,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
    },
    onDropAccepted: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        processFile(acceptedFiles[0]);
      }
    }
  });

  const hasAttachment = attachmentState.fileName !== null;
  const { isProcessing } = attachmentState;

  const handleMicClick = () => {
    if (!isRecording) {
      startRecordAudio();
      return;
    }

    const selectedImages = getSelectedImages();

    submitRecordedAudio(attachmentState.images.length > 0 && selectedImages.length > 0 ? selectedImages : []);
    removeFile();
  };

  return (
    <Grid
      item
      container
      xs
      className={clsx("dropzone", classes.messageContainer)}
      alignItems="flex-end"
      wrap="nowrap"
      {...getRootProps()}
    >
      <input {...getInputProps()} />

      {/* LEFT COLUMN: Content and controls */}
      <Grid
        item
        container
        direction="column"
        style={{ gap: '5px', flex: 1, minWidth: 0 }}
        data-testid="assistant-prompt-left-column"
      >
        <Grid item style={{ width: "100%" }}>
          <Grid item container className={classes.message} direction="column" spacing={1} style={{ width: "100%" }}>
            <Grid item style={{ width: "100%" }}>
              <PromptSelector {...promptSelector} disabled={contentIsLoading || isProcessing} textColor={textColor} />
            </Grid>
            <Grid item style={{ width: "100%" }}>
              <TextField
                fullWidth
                multiline
                disabled={contentIsLoading || isProcessing}
                placeholder={
                   hasAttachment
                     ? `${attachmentState.fileName} - Add instructions or press Enter`
                     : t('questionsAndAnswers:assistantPlaceholder')
                 }
                inputRef={questionInputRef}
                inputProps={{ 'data-paste-cleaner-ignore': 'true' }}
                InputProps={{
                  disableUnderline: true,
                  style: { color: textColor, fontSize: "0.8rem" },
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !contentIsLoading && !isProcessing) {
                      e.preventDefault();
                      handleSend();
                    }
                  },
                }}
              />

              {contentIsLoading && (
                <LinearProgress className={classes.progressBar} />
              )}

              {isProcessing && (
                <Box style={{ padding: '8px 0' }}>
                  <Box display="flex" alignItems="center" style={{ gap: '8px', marginBottom: '4px' }}>
                    <CircularProgress size={14} style={{ color: '#FB8C00' }} />
                    <Typography variant="caption" style={{ color: textColor, opacity: 0.7 }}>
                      {attachmentState.statusText}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={attachmentState.progress}
                    className={classes.progressBar}
                  />
                </Box>
              )}

              {hasAttachment && !isProcessing && (
                <Box display="flex" alignItems="center" style={{ padding: '4px 0' }}>
                  <FileChip
                    fileType={attachmentState.fileType}
                    label={attachmentState.fileName ?? ""}
                    onDelete={removeFile}
                    textColor={textColor}
                  />
                </Box>
              )}

              {attachmentState.images.length > 0 && !isProcessing && (
                <ImagePreviews
                  images={attachmentState.images}
                  onToggleSelection={toggleSelection}
                  onUpdateSendAs={updateSendAs}
                  onViewImagePreview={onViewImagePreview}
                  textColor={textColor}
                />
              )}
            </Grid>
            <Grid item container wrap="nowrap" alignItems="center">
              <Grid item>
                <FormControlLabel
                  control={<Checkbox className={classes.checkbox} onChange={toogleAudioEnable} />}
                  label={
                    <Typography variant="caption" color="primary" style={{ color: "white", lineHeight: "0px", paddingLeft: "0.2rem" }}>
                      {t('questionsAndAnswers:audio')}
                    </Typography>
                  }
                  style={{ marginLeft: 0, whiteSpace: 'nowrap' }}
                />
              </Grid>
              <Grid item style={{ flex: 1 }} />
              <Grid item container spacing={1} justifyContent="flex-end">
                <Grid item>
                  <IconButton
                    size="small"
                    disabled={contentIsLoading || isProcessing}
                    onClick={open}
                    title="Upload document (PDF or image)"
                  >
                    <FontAwesomeIcon size="sm" icon="paperclip" color="white" />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton
                    size="small"
                    disabled={contentIsLoading || isProcessing}
                    onClick={handleSend}
                  >
                    <FontAwesomeIcon size="sm" icon="paper-plane" color="white" />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* RIGHT COLUMN: Actions */}
      <Box
        display="flex"
        flexDirection="column"
        alignSelf="stretch"
        justifyContent="center"
        position="relative"
        data-testid="assistant-prompt-actions-column"
      >
        {isAudioActive && (
          <IconButton
            onClick={onStopAudio}
            title="Stop audio"
            aria-label="Stop audio"
            style={{
              padding: '8px',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(calc(-100% - 28px))',
              left: 0,
              right: 0
            }}
            size="large"
          >
            <FontAwesomeIcon size="1x" icon="stop-circle" color="white" />
          </IconButton>
        )}
        {contentIsLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none'
            }}
          >
            <CircularProgress size={24} style={{ color: 'white' }} />
          </Box>
        )}
        <IconButton
          onClick={handleMicClick}
          disabled={contentIsLoading || isProcessing}
          style={{ padding: '8px' }}
          size="large"
        >
          {isRecording && (<FontAwesomeIcon size="1x" icon="circle" color="tomato" />)}
          {!isRecording && (<FontAwesomeIcon size="1x" icon="microphone" color="white" />)}
        </IconButton>
      </Box>
    </Grid>
  );
};
